import React from "react";

import { useTranslation } from "react-i18next";

import ButtonTemplates from "./features/button/ButtonTemplates";
import AnnouncementsTemplates from "./features/announcements/AnnouncementsTemplates";
import CardsTemplates from "./features/cards/CardsTemplates";
import CountdownTemplates from "./features/countdown/CountdownTemplates";
import PanelsTemplates from "./features/panels/PanelsTemplates";
import ImageSliderTemplates from "./features/image-slider/ImageSliderTemplates";
import TooltipFootnoteTemplates from "./features/tooltip-footnote/TooltipFootnoteTemplates";
import AdvancedTabsGroupTemplates from "./features/advanced-tabs/group/AdvancedTabsGroupTemplates";
import PanelAdvancedTemplates from "./features/panel-advanced/PanelAdvancedTemplates";
import {
  TemplatesActionButton,
  TemplatesCollapsibleContainer,
  TemplatesContainer,
  TemplatesContentContainer,
  TemplatesContentHeader,
  AnimationContainer,
} from "./styles";
import { useOpticsStore } from "../../../../stores/use-optics-store";
import { MACRO_KEY } from "../../../defaults";
import { MACRO_WITH_TEMPLATES } from "./defaults";
import { useClickOutside } from "@Shared/Hooks/useClickOutside";
import { useRef } from "react";
import { macroStores } from "../../../../stores/export-stores";

const templateComponents = {
  undefined: () => <></>,
  [MACRO_KEY.BUTTON]: ButtonTemplates,
  [MACRO_KEY.BUTTON_INLINE]: ButtonTemplates,
  [MACRO_KEY.ANNOUNCEMENTS]: AnnouncementsTemplates,
  [MACRO_KEY.CARDS]: CardsTemplates,
  [MACRO_KEY.COUNTDOWN]: CountdownTemplates,
  [MACRO_KEY.PANEL_SIMPLE]: PanelsTemplates,
  [MACRO_KEY.IMAGE_SLIDER]: ImageSliderTemplates,
  [MACRO_KEY.TOOLTIPS_AND_FOOTNOTES]: TooltipFootnoteTemplates,
  [MACRO_KEY.ADVANCED_TABS_GROUP]: AdvancedTabsGroupTemplates,
  [MACRO_KEY.PANEL_ADVANCED]: PanelAdvancedTemplates,
};

const Templates = () => {
  const templatesDrawerRef = useRef();

  const { t } = useTranslation();
  const { drawers, setDrawers, macro, isDrawerModalOpen } = useOpticsStore(
    (state) => ({
      drawers: state.drawers,
      setDrawers: state.setDrawers,
      macro: state.macro,
      isDrawerModalOpen: state.isDrawerModalOpen,
    })
  );
  const macroData = macroStores[macro]?.((state) => state?.data || {});
  const hasMacroData = Array.isArray(macroData)
    ? !!macroData.length
    : !!Object.keys(macroData).length;

  const hasTemplates = MACRO_WITH_TEMPLATES.includes(macro);

  const isCollapsed = !drawers.templates || !hasTemplates;

  const TemplateComponent = templateComponents[macro];

  useClickOutside(
    templatesDrawerRef,
    () => {
      if (drawers.templates && !isDrawerModalOpen) {
        setDrawers({ templates: false, settings: hasMacroData });
      }
    },
    "optics-sidebar"
  );

  return (
    <TemplatesCollapsibleContainer
      hasTemplates={hasTemplates}
      isCollapsed={isCollapsed}
      ref={templatesDrawerRef}
      macro={macro}
    >
      <TemplatesContainer
        isCollapsed={isCollapsed}
        onClick={() => {
          if (isCollapsed && hasTemplates) {
            setDrawers({
              templates: true,
              settings: false,
            });
          }
        }}
      >
        {hasTemplates && (
          <TemplatesActionButton
            onClick={() => {
              if (!isCollapsed) {
                setDrawers({ templates: false, settings: hasMacroData });
              }
            }}
          >
            {t("templates")}
          </TemplatesActionButton>
        )}

        <AnimationContainer isCollapsed={isCollapsed}>
          {drawers.templates && hasTemplates && (
            <TemplatesContentContainer>
              <TemplatesContentHeader>
                <h1 style={{ margin: 0, fontWeight: 400, fontSize: "21px" }}>
                  {t("templates")}
                </h1>
              </TemplatesContentHeader>
              <TemplateComponent editorMacro={macro} />
            </TemplatesContentContainer>
          )}
        </AnimationContainer>
      </TemplatesContainer>
    </TemplatesCollapsibleContainer>
  );
};

export default Templates;
